import { useEffect } from 'react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { SiteCodes } from 'shared-types'
import 'swiper/css'
import 'swiper/css/pagination'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import CustomerMyRewardsProgressCard from '~/components/CustomerMyRewardsProgressCard/CustomerMyRewardsProgressCard'
import { Drawer } from '~/components/Drawer'
import { Link } from '~/components/Link'
import { LoadingSpinner } from '~/components/LoadingSpinner'
import OrderDeliveryCard from '~/components/OrderDeliveryCard/OrderDeliveryCard'
import RichText from '~/components/RichText/RichText'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { useAuth, useCompareProducts, useCustomer, usePage } from '~/hooks'
import { useCustomerRewards } from '~/hooks/useCustomerRewards'

import { Client } from '../../customClients/client'
import { Button } from '../Button'
import styles from './AccountQuickViewDrawer.module.css'
import { AccountQuickViewDrawerProps } from './AccountQuickViewDrawer.types'

const AUTHED_ROUTES = ['/checkout/:id']

export const AccountQuickViewDrawer = ({
  open,
  onClose,
}: AccountQuickViewDrawerProps) => {
  const page = usePage()
  const { customer } = useCustomer()
  const { customerRewards, isLoading: isLoadingRewards } = useCustomerRewards(
    'AccountQuickViewDrawer',
    open,
    page?.siteCode === SiteCodes.RTM
  )
  const { clearAllProducts } = useCompareProducts()
  const { logOut } = useAuth()
  const router = useRouter()

  useEffect(() => {
    const handleRouteChange = () => {
      onClose?.()
    }
    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [onClose, router])

  if (!customer) {
    return null
  }
  const { customerRecentOrders } = customer

  const renderHeading = (title: string, link: string) => {
    return (
      <div className='flex items-center justify-between text-primary-blue'>
        <Typography
          className='!font-bold !text-base'
          tag={TypographyTag.div}
          variant={TypographyVariant.BodyRegular}>
          {title}
        </Typography>
        <Link href={link} title='View all'>
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyRegular}
            className='!underline !font-bold'>
            View all
          </Typography>
        </Link>
      </div>
    )
  }

  const checkIsAuthorizedRoute = () => {
    const currentRoute = router.asPath.split(/\?|\#/)[0].trim()
    if (currentRoute === '/') {
      return false
    }

    const currentRouteArr = currentRoute.split('/')
    return AUTHED_ROUTES.some((route) => {
      const routeArr = route.split('/')
      if (currentRouteArr.length !== routeArr.length) {
        return false
      }

      const hasNoMatched = routeArr.some((r, index) => {
        if (index > 1 && r.startsWith(':')) {
          return false
        }

        return r !== currentRouteArr[index]
      })

      return !hasNoMatched
    })
  }

  const signout = async () => {
    await logOut()

    if (checkIsAuthorizedRoute()) {
      await router.push('/')
      router.reload()
    } else {
      router.reload()
    }

    onClose?.()
    clearAllProducts()
    Client.addressfinder.clearCache()
  }

  const renderTitle = () => {
    return (
      <div className='flex items-center gap-1'>
        <Typography
          variant={TypographyVariant.Heading5}
          tag={TypographyTag.div}>
          Hi {customer?.firstName}
        </Typography>
        <Button variant='ghost' onClick={signout}>
          <Typography
            className='!underline !font-bold !text-base'
            tag={TypographyTag.span}
            variant={TypographyVariant.BodyRegular}>
            Logout
          </Typography>
        </Button>
      </div>
    )
  }

  const renderAccountInfo = () => {
    const accountShortcuts =
      page?.accountDrawerConfiguration?.accountShortcuts || []
    const gridColumnsCss = `repeat(${accountShortcuts.length}, 1fr)`

    return (
      <div className='px-4 mb-4'>
        <div className='mb-4'>
          {renderHeading('Account Info', '/account/dashboard')}
        </div>
        <div
          className='grid gap-4'
          style={{ gridTemplateColumns: gridColumnsCss }}>
          {accountShortcuts.map((shortcut, index) => {
            const iconUrl = shortcut.iconUrl
              ? `url(${shortcut.iconUrl})`
              : undefined

            return (
              <div
                key={index}
                className={classNames(
                  'relative',
                  'rounded-md border border-[#E0E0E0]',
                  'flex-grow flex items-center gap-2',
                  'h-[64px] py-1 px-2'
                )}>
                <Link className='absolute z-20 inset-0' href={shortcut.link} />
                <div
                  className='rounded-full min-h-[24px] min-w-[24px] h-[24px] w-[24px]'
                  style={{
                    backgroundColor: shortcut.backgroundColor,
                    backgroundImage: iconUrl,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
                <Typography
                  className='!text-black !font-bold'
                  tag={TypographyTag.div}
                  variant={TypographyVariant.BodyRegular}>
                  {shortcut.label}
                </Typography>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderLoyaltyPoints = () => {
    if (
      page?.siteCode === SiteCodes.RTM ||
      customer.customerType !== 'JYCRWD'
    ) {
      return null
    }

    let tmpCustomerRewards = customerRewards

    if (isLoadingRewards) {
      tmpCustomerRewards = {
        rewardPoints: 0,
        rewards: [
          {
            rewardPoints: 0,
            remainingPoints: 0,
            rewardTiers: [0],
          },
        ],
      }
    }

    return (
      <div className='px-4 mb-6 relative'>
        {isLoadingRewards && (
          <div
            className={classNames(
              'absolute inset-0 z-20 bg-white/75',
              'flex items-center justify-center'
            )}>
            <LoadingSpinner />
          </div>
        )}
        {tmpCustomerRewards?.rewards?.map((reward, index) => {
          return (
            <div key={index}>
              <CustomerMyRewardsProgressCard
                showRemainingPoints={false}
                currentPoints={customerRewards?.rewardPoints}
                tiers={reward?.rewardTiers?.map((point) => {
                  return {
                    points: point,
                  }
                })}
              />
            </div>
          )
        })}
      </div>
    )
  }

  const renderRecentOrders = () => {
    if (!customerRecentOrders?.length) {
      return null
    }

    return (
      <div
        className={classNames(
          'px-4 md:pl-6 md:pr-0 pt-6',
          'border-t border-greyscale-300',
          'mb-4'
        )}>
        <div className='mb-4 pr-0 md:pr-6'>
          {renderHeading('My Orders', '/account/orders')}
        </div>
        <Swiper
          modules={[Pagination]}
          pagination={{
            clickable: true,
            bulletClass:
              'inline-block w-2 h-2 rounded-full bg-greyscale-300 opacity-1 mx-1',
            bulletActiveClass: '!bg-state-success-light',
          }}
          spaceBetween={8}
          slidesPerView={1}
          breakpoints={{
            768: {
              spaceBetween: 16,
              slidesPerView: 1.1,
            },
          }}>
          {!!customerRecentOrders?.length &&
            customerRecentOrders.map((order, index) => {
              return (
                <SwiperSlide key={index} className='w-3/5 mb-10'>
                  <OrderDeliveryCard {...order} />
                </SwiperSlide>
              )
            })}
        </Swiper>
      </div>
    )
  }

  const renderHelpCentreMessage = () => {
    const helpCentreMessage =
      page?.accountDrawerConfiguration?.helpCentreMessage?.json

    if (!helpCentreMessage) {
      return null
    }

    return (
      <div className='px-4 md:px-6 !leading-none'>
        <RichText content={helpCentreMessage} />
      </div>
    )
  }

  return (
    <Drawer
      width={506}
      wrapperClass={classNames(styles.wrapper, '')}
      title={renderTitle()}
      isOpen={open}
      placement='right'
      onClose={onClose}>
      <div
        className={classNames(
          'flex-1 w-full pt-6 overflow-y-auto',
          styles.containerWrapper
        )}>
        {renderAccountInfo()}
        {renderLoyaltyPoints()}
        {renderRecentOrders()}
        {renderHelpCentreMessage()}
      </div>
    </Drawer>
  )
}
